import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, CreditCard, Banknote, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { invitationService } from '../services/api';
import PayPalInvitationButton from '../components/PayPalInvitationButton';

interface BookingData {
  id: number;
  code_reservation: string;
  apartment: {
    id: number;
    titre: string;
    adresse: string;
    ville: string;
    photo_principale?: string;
  };
  date_debut: string;
  date_fin: string;
  nb_nuits: number;
  nb_personnes: number;
  montant_total: number;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  status: string;
}

const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'cash'>('paypal');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('Token d\'invitation invalide');
        setLoading(false);
        return;
      }

      try {
        const bookingRes = await invitationService.view(token);
        if (bookingRes.data?.booking) {
          setBooking(bookingRes.data.booking);
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Invitation non trouvée ou expirée');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleCashPayment = async () => {
    if (!token) return;
    setProcessing(true);
    try {
      await invitationService.confirm(token, { payment_method: 'cash' });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('fr-FR') + ' FCFA';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement de l'invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invitation invalide</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Réservation confirmée !</h1>
          <p className="text-gray-600 mb-2">
            Votre réservation <strong>{booking?.code_reservation}</strong> a été confirmée.
          </p>
          {paymentMethod === 'cash' && (
            <p className="text-sm text-gray-500 mb-6">
              Le gérant a été notifié et vous contactera pour finaliser le paiement.
            </p>
          )}
          {paymentMethod === 'paypal' && (
            <p className="text-sm text-gray-500 mb-6">
              Vous recevrez un email de confirmation avec votre facture.
            </p>
          )}
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Confirmer votre réservation</h1>
          <p className="text-gray-600 mt-2">Code: <strong>{booking.code_reservation}</strong></p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Détails de la réservation */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Détails de la réservation</h2>
            
            {booking.apartment && (
              <div className="mb-4">
                <h3 className="font-semibold text-lg">{booking.apartment.titre}</h3>
                <p className="text-gray-600 flex items-center gap-1 text-sm">
                  <MapPin className="w-4 h-4" />
                  {booking.apartment.adresse}, {booking.apartment.ville}
                </p>
              </div>
            )}

            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Arrivée</p>
                  <p className="font-medium">{formatDate(booking.date_debut)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Départ</p>
                  <p className="font-medium">{formatDate(booking.date_fin)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Voyageurs</p>
                  <p className="font-medium">{booking.nb_personnes} personne(s) • {booking.nb_nuits} nuit(s)</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total à payer</span>
                <span className="text-2xl font-bold text-primary-600">{formatPrice(booking.montant_total)}</span>
              </div>
            </div>
          </div>

          {/* Paiement */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Choisir le mode de paiement</h2>

            <div className="space-y-3 mb-6">
              <label
                className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition ${
                  paymentMethod === 'paypal' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  value="paypal"
                  checked={paymentMethod === 'paypal'}
                  onChange={() => setPaymentMethod('paypal')}
                  className="w-4 h-4 text-primary-500"
                />
                <CreditCard className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium">PayPal</p>
                  <p className="text-sm text-gray-500">Paiement sécurisé en ligne</p>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition ${
                  paymentMethod === 'cash' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={() => setPaymentMethod('cash')}
                  className="w-4 h-4 text-primary-500"
                />
                <Banknote className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium">Espèces</p>
                  <p className="text-sm text-gray-500">Paiement sur place au gérant</p>
                </div>
              </label>
            </div>

            {paymentMethod === 'paypal' && token && (
              <PayPalInvitationButton
                token={token}
                amount={booking.montant_total}
                onSuccess={() => setSuccess(true)}
                onError={(err) => setError(err?.message || 'Erreur lors du paiement')}
              />
            )}

            {paymentMethod === 'cash' && (
              <button
                onClick={handleCashPayment}
                disabled={processing}
                className="w-full btn btn-primary py-3 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Confirmation en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirmer la réservation
                  </>
                )}
              </button>
            )}

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Infos client */}
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <h3 className="font-medium text-gray-700 mb-2">Informations client</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Nom:</span>
              <span className="ml-2 font-medium">{booking.guest_name}</span>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="ml-2 font-medium">{booking.guest_email}</span>
            </div>
            <div>
              <span className="text-gray-500">Téléphone:</span>
              <span className="ml-2 font-medium">{booking.guest_phone}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationPage;
